import { getRfqHubFileRelease } from './rfqHubFileReleaseService';
import { collection, doc, getDoc, getDocs, query, where } from 'firebase/firestore';
import { getDownloadURL, ref } from 'firebase/storage';
import { db, storage } from './firebase';
import {
  buildProposalCsvPreviewTable,
  parseTimberCsvForRfqHub,
  cadworkParsedItemsToNodeSets,
  DEFAULT_PROPOSAL_TIMBER_GRADE,
  DEFAULT_PROPOSAL_TIMBER_SPECIES,
  type CSVParseResult,
  type ParsedTimberItem,
  type TimberCsvParseOptions,
} from './proposalCSVParser';
import type { ProposalNodeSet, RfqHubFileRelease } from '../types';
import { overlayProposalTimberGradesOnNodeSets } from './rfqHubTimberGradeOverlay';
import { lineKey } from './proposalTimberDiff';
import { parseTimberMaterialColumn } from './timberMaterialParser';
import { loadLinkedProposalNodeSets } from './proposalRfqVersionLink';
import { buildTimberSupplierCsvPreviewTable } from './supplierTimberExport';
import { alignNodeSetsToStagedStructures } from './rfqPublishStructureReview';

export interface HubTimberImportResult {
  nodeSets: ProposalNodeSet[];
  /** Hub file display name (for labels / errors). */
  sourceFileName: string;
}

type HubFileRow = {
  kindTag?: string;
  isHeadVersion?: boolean;
  isCurrentTimberList?: boolean;
  uploadedAt: Date;
  name: string;
  storagePath: string;
  trueDimensions?: boolean;
};

/**
 * Live project-file flag when the CRM can read it. Null means unknown, so a
 * staged release can fall back to the value copied at stage time.
 */
export async function resolveTimberCsvTrueDimensions(
  sourceFileId?: string,
  stagedFallback?: boolean,
): Promise<boolean> {
  const id = (sourceFileId || '').trim();
  if (id) {
    try {
      const snap = await getDoc(doc(db, 'projectFiles', id));
      if (snap.exists()) return snap.data()?.trueDimensions === true;
    } catch {
      /* Supplier sessions may not be allowed to read project files. */
    }
  }
  return stagedFallback === true;
}

function nodeSetsFromParsedCsv(
  parsed: CSVParseResult,
  options?: { singleStructureName?: string },
): ProposalNodeSet[] {
  return cadworkParsedItemsToNodeSets(parsed.items, options);
}

/** Re-sync species/grade on export node sets from parsed CSV material (authoritative for publish). */
export function syncNodeSetGradesFromParsedItems(
  nodeSets: ProposalNodeSet[],
  items: ParsedTimberItem[],
): ProposalNodeSet[] {
  if (!items.length || !nodeSets.length) return nodeSets;

  const byLineNumber = new Map<number, ParsedTimberItem>();
  const byMatchKey = new Map<string, ParsedTimberItem>();
  for (const row of items) {
    byLineNumber.set(row.lineNumber, row);
    byMatchKey.set(lineKey(row.pieceName, row.normalizedDimensions), row);
  }

  const resolveParsedRow = (line: ProposalNodeSet['materials']['timberList'][number]): ParsedTimberItem | undefined => {
    if (line.sourceLineNumber != null) {
      const byLine = byLineNumber.get(line.sourceLineNumber);
      if (byLine && lineKey(byLine.pieceName, byLine.normalizedDimensions) === lineKey(line.name, line.dimensions)) {
        return byLine;
      }
    }
    return byMatchKey.get(lineKey(line.name, line.dimensions));
  };

  return nodeSets.map(ns => ({
    ...ns,
    materials: {
      ...ns.materials,
      timberList: (ns.materials.timberList ?? []).map(line => {
        const parsedRow = resolveParsedRow(line);
        if (!parsedRow) return line;
        const { species, grade } = parseTimberMaterialColumn(parsedRow.material, {
          speciesFallback: DEFAULT_PROPOSAL_TIMBER_SPECIES,
          gradeFallback: DEFAULT_PROPOSAL_TIMBER_GRADE,
        });
        return { ...line, species, grade };
      }),
    },
  }));
}

function nodeSetsForSupplierExport(
  parsed: CSVParseResult,
  options?: { singleStructureName?: string },
  proposalNodeSets?: ProposalNodeSet[] | null,
): ProposalNodeSet[] {
  const fromCsv = syncNodeSetGradesFromParsedItems(
    nodeSetsFromParsedCsv(parsed, options),
    parsed.items,
  );
  return applyProposalTimberOverlayToNodeSets(fromCsv, proposalNodeSets);
}

/**
 * Node sets behind a supplier CSV attachment, parsed the same way a staged hub
 * release is (Cadwork rows, then proposal grade overlay when a proposal is open).
 */
export function nodeSetsFromTimberCsvText(
  csvText: string,
  proposalNodeSets?: ProposalNodeSet[] | null,
  options?: TimberCsvParseOptions,
): { nodeSets: ProposalNodeSet[]; parseErrors: string[] } {
  const parsed = parseTimberCsvForRfqHub(csvText, options);
  if (parsed.items.length === 0) {
    return {
      nodeSets: [],
      parseErrors: parsed.errors.length ? parsed.errors : ['No timber rows parsed from CSV.'],
    };
  }
  return {
    nodeSets: nodeSetsForSupplierExport(parsed, undefined, proposalNodeSets),
    parseErrors: parsed.errors,
  };
}

export function applyProposalTimberOverlayToNodeSets(
  nodeSets: ProposalNodeSet[],
  proposalNodeSets: ProposalNodeSet[] | null | undefined,
): ProposalNodeSet[] {
  if (!proposalNodeSets?.length) return nodeSets;
  return overlayProposalTimberGradesOnNodeSets(nodeSets, proposalNodeSets);
}

function stagedNodeSetsHaveTimber(nodeSets: ProposalNodeSet[]): boolean {
  return nodeSets.some((ns) => (ns.materials?.timberList?.length ?? 0) > 0);
}

function enabledStagedNodeSets(nodeSets: ProposalNodeSet[]): ProposalNodeSet[] {
  return nodeSets.filter((ns) => ns.enabled !== false);
}

/** Prefer linked proposal snapshot for proposal-push staging; fall back to parsing staged CSV. */
export async function loadStagedReleaseNodeSets(
  release: RfqHubFileRelease,
  dealId: string,
  proposalNodeSets?: ProposalNodeSet[] | null,
): Promise<{ nodeSets: ProposalNodeSet[]; sourceFileName: string; parseErrors: string[]; trueDimensions: boolean }> {
  const sourceFileName = release.fileName;
  const trueDimensions = await resolveTimberCsvTrueDimensions(release.sourceFileId, release.trueDimensions);
  const linkedId = release.linkedProposalVersionId?.trim();
  if (linkedId && dealId) {
    const linked = await loadLinkedProposalNodeSets(dealId, { linkedProposalVersionId: linkedId });
    if (linked?.length) {
      const nodeSets = enabledStagedNodeSets(linked);
      if (stagedNodeSetsHaveTimber(nodeSets)) {
        return { nodeSets, sourceFileName, parseErrors: [], trueDimensions };
      }
    }
  }

  if (!release.releasedUrl) {
    throw new Error('Release file is unavailable.');
  }
  const text = await fetch(release.releasedUrl).then((r) => {
    if (!r.ok) throw new Error('Failed to download staged timber CSV.');
    return r.text();
  });
  const parsed = parseTimberCsvForRfqHub(text, { trueDimensions });
  if (parsed.items.length === 0) {
    throw new Error(parsed.errors.join(' ') || 'No timber rows parsed from staged CSV.');
  }
  return {
    nodeSets: alignNodeSetsToStagedStructures(
      nodeSetsForSupplierExport(parsed, undefined, proposalNodeSets),
      release,
    ),
    sourceFileName,
    parseErrors: parsed.errors,
    trueDimensions,
  };
}

async function loadStagedReleasePreviewFromSnapshot(
  release: RfqHubFileRelease,
  dealId: string,
): Promise<HubTimberPublishPreview | null> {
  const linkedId = release.linkedProposalVersionId?.trim();
  if (!linkedId || !dealId) return null;
  const linked = await loadLinkedProposalNodeSets(dealId, { linkedProposalVersionId: linkedId });
  if (!linked?.length) return null;
  const nodeSets = enabledStagedNodeSets(linked);
  if (!stagedNodeSetsHaveTimber(nodeSets)) return null;

  const preview = buildTimberSupplierCsvPreviewTable({ nodeSets }, undefined, 60);
  const parsedItemCount = nodeSets.reduce(
    (sum, ns) => sum + (ns.materials?.timberList?.length ?? 0),
    0,
  );

  return {
    sourceFileName: release.fileName,
    canPublish: true,
    nodeSets,
    previewRows: preview.rows,
    totalDataRows: preview.totalRows,
    parsedItemCount,
    parseErrors: [],
    csvDelimiterLabel: 'COMMA',
  };
}

async function resolveHubTimberCsvText(
  timberProjectId: string,
): Promise<{ sourceFileName: string; text: string; trueDimensions: boolean }> {
  const q = query(collection(db, 'projectFiles'), where('projectId', '==', timberProjectId));
  const snap = await getDocs(q);

  const files: HubFileRow[] = snap.docs.map((d) => {
    const x = d.data() as Record<string, unknown>;
    const uploadedAtRaw = x.uploadedAt as { toDate?: () => Date } | undefined;
    return {
      kindTag: x.kindTag as string | undefined,
      isHeadVersion: x.isHeadVersion === true,
      isCurrentTimberList: x.isCurrentTimberList === true,
      uploadedAt: uploadedAtRaw?.toDate?.() ?? new Date(0),
      name: (x.name as string) || 'timber.csv',
      storagePath: (x.storagePath as string) || '',
      trueDimensions: x.trueDimensions === true,
    };
  });

  const candidates = files.filter(
    (f) => f.kindTag === 'timber_list' && f.storagePath && (f.isHeadVersion || f.isCurrentTimberList),
  );

  if (candidates.length === 0) {
    throw new Error(
      'No timber list CSV found in Project Hub for this job. Upload a timber list in Project Hub → Files (use the timber tag and version history).',
    );
  }

  candidates.sort((a, b) => b.uploadedAt.getTime() - a.uploadedAt.getTime());
  const file = candidates[0];

  const url = await getDownloadURL(ref(storage, file.storagePath));
  const text = await fetch(url).then((r) => {
    if (!r.ok) throw new Error('Failed to download timber CSV from storage.');
    return r.text();
  });

  return { sourceFileName: file.name, text, trueDimensions: file.trueDimensions === true };
}

export interface HubTimberPublishPreview {
  sourceFileName: string;
  /** Ready for `publishRfqPackage` when true. */
  canPublish: boolean;
  nodeSets: ProposalNodeSet[];
  previewRows: string[][];
  totalDataRows: number;
  parsedItemCount: number;
  parseErrors: string[];
  csvDelimiterLabel: string;
}

/**
 * Load hub timber CSV once for a publish preview (table + parsed node sets).
 */
export async function loadHubTimberListPublishPreview(
  timberProjectId: string,
  structureName = 'Project Hub timber list',
  proposalNodeSets?: ProposalNodeSet[] | null,
): Promise<HubTimberPublishPreview> {
  const { sourceFileName, text, trueDimensions } = await resolveHubTimberCsvText(timberProjectId);
  const parsed = parseTimberCsvForRfqHub(text, { trueDimensions });
  const { rows, totalDataLines, delimiterLabel } = buildProposalCsvPreviewTable(text, 60);

  if (parsed.items.length === 0) {
    return {
      sourceFileName,
      canPublish: false,
      nodeSets: [],
      previewRows: rows,
      totalDataRows: totalDataLines,
      parsedItemCount: 0,
      parseErrors: parsed.errors.length ? parsed.errors : ['No timber rows parsed from CSV.'],
      csvDelimiterLabel: delimiterLabel,
    };
  }

  const nodeSets = nodeSetsForSupplierExport(
    parsed,
    { singleStructureName: structureName },
    proposalNodeSets,
  );

  return {
    sourceFileName,
    canPublish: true,
    nodeSets,
    previewRows: rows,
    totalDataRows: totalDataLines,
    parsedItemCount: parsed.totalItems,
    parseErrors: parsed.errors,
    csvDelimiterLabel: delimiterLabel,
  };
}

/**
 * Load the latest Project Hub timber-list CSV head (version control) and convert
 * to proposal node sets for supplier RFQ export (CSV/PDF).
 */
export async function loadHubTimberListNodeSets(
  timberProjectId: string,
  structureName = 'Project Hub timber list',
  proposalNodeSets?: ProposalNodeSet[] | null,
): Promise<HubTimberImportResult> {
  const { sourceFileName, text, trueDimensions } = await resolveHubTimberCsvText(timberProjectId);
  const parsed = parseTimberCsvForRfqHub(text, { trueDimensions });
  if (parsed.items.length === 0) {
    const err = parsed.errors.join(' ') || 'No timber rows parsed from CSV.';
    throw new Error(err);
  }

  return {
    nodeSets: nodeSetsForSupplierExport(
      parsed,
      { singleStructureName: structureName },
      proposalNodeSets,
    ),
    sourceFileName,
  };
}

async function resolveStagedTimberCsvText(
  rfqProjectId: string,
  hubFileReleaseId: string,
): Promise<{ release: RfqHubFileRelease; sourceFileName: string; text: string; trueDimensions: boolean }> {
  const release = await getRfqHubFileRelease(rfqProjectId, hubFileReleaseId);
  if (!release) {
    throw new Error('Staged hub timber release not found.');
  }
  if (release.workType !== 'timber_rfq') {
    throw new Error('Selected release is not a timber list.');
  }

  const text = await fetch(release.releasedUrl).then((r) => {
    if (!r.ok) throw new Error('Failed to download staged timber CSV.');
    return r.text();
  });
  const trueDimensions = await resolveTimberCsvTrueDimensions(release.sourceFileId, release.trueDimensions);

  return { release, sourceFileName: release.fileName, text, trueDimensions };
}

/**
 * Load a staged hub timber CSV for publish preview.
 */
export async function loadStagedTimberListPublishPreview(
  rfqProjectId: string,
  hubFileReleaseId: string,
  _structureName = 'Staged hub timber list',
  proposalNodeSets?: ProposalNodeSet[] | null,
): Promise<HubTimberPublishPreview> {
  const { release, sourceFileName, text, trueDimensions } = await resolveStagedTimberCsvText(rfqProjectId, hubFileReleaseId);
  const dealId = (release.dealId || '').trim();
  const fromSnapshot = await loadStagedReleasePreviewFromSnapshot(release, dealId);
  if (fromSnapshot) return fromSnapshot;

  const parsed = parseTimberCsvForRfqHub(text, { trueDimensions });
  const { rows, totalDataLines, delimiterLabel } = buildProposalCsvPreviewTable(text, 60);

  if (parsed.items.length === 0) {
    return {
      sourceFileName,
      canPublish: false,
      nodeSets: [],
      previewRows: rows,
      totalDataRows: totalDataLines,
      parsedItemCount: 0,
      parseErrors: parsed.errors.length ? parsed.errors : ['No timber rows parsed from CSV.'],
      csvDelimiterLabel: delimiterLabel,
    };
  }

  const nodeSets = alignNodeSetsToStagedStructures(
    nodeSetsForSupplierExport(parsed, undefined, proposalNodeSets),
    release,
  );

  return {
    sourceFileName,
    canPublish: true,
    nodeSets,
    previewRows: rows,
    totalDataRows: totalDataLines,
    parsedItemCount: parsed.totalItems,
    parseErrors: parsed.errors,
    csvDelimiterLabel: delimiterLabel,
  };
}

function dedupeNodeSetsById(nodeSets: ProposalNodeSet[]): ProposalNodeSet[] {
  const seen = new Set<string>();
  const out: ProposalNodeSet[] = [];
  for (const ns of nodeSets) {
    const key = ns.id || `${ns.name}`;
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(ns);
  }
  return out;
}

/**
 * Node sets for every staged release a package spans. Files → RFQs rounds publish
 * ONE package per supplier across one release per structure, so re-scoping or
 * acknowledging that package must rebuild its snapshot from all of them — reading
 * just the clicked release would shrink the supplier's timber list to one structure.
 */
export async function loadPackageTimberListNodeSets(
  rfqProjectId: string,
  hubFileReleaseIds: string[],
  proposalNodeSets?: ProposalNodeSet[] | null,
): Promise<{ nodeSets: ProposalNodeSet[]; sourceFileNames: string[] }> {
  const ids = Array.from(new Set(hubFileReleaseIds.map(id => (id || '').trim()).filter(Boolean)));
  const loaded = await Promise.all(
    ids.map(id => loadStagedTimberListNodeSets(rfqProjectId, id, undefined, proposalNodeSets)),
  );
  return {
    nodeSets: dedupeNodeSetsById(loaded.flatMap(l => l.nodeSets)),
    sourceFileNames: loaded.map(l => l.sourceFileName),
  };
}

/**
 * Publish preview for a package that may span several staged releases: single
 * release → the regular staged preview; several → node sets from every release,
 * with the preview table rebuilt from the combined list.
 */
export async function loadPackageTimberListPublishPreview(
  rfqProjectId: string,
  hubFileReleaseIds: string[],
  structureName = 'Staged hub timber list',
  proposalNodeSets?: ProposalNodeSet[] | null,
): Promise<HubTimberPublishPreview> {
  const ids = Array.from(new Set(hubFileReleaseIds.map(id => (id || '').trim()).filter(Boolean)));
  if (ids.length <= 1) {
    return loadStagedTimberListPublishPreview(rfqProjectId, ids[0] ?? '', structureName, proposalNodeSets);
  }
  const { nodeSets, sourceFileNames } = await loadPackageTimberListNodeSets(rfqProjectId, ids, proposalNodeSets);
  const preview = buildTimberSupplierCsvPreviewTable({ nodeSets }, undefined, 60);
  return {
    sourceFileName: sourceFileNames.join(', '),
    canPublish: nodeSets.length > 0,
    nodeSets,
    previewRows: preview.rows,
    totalDataRows: preview.totalRows,
    parsedItemCount: nodeSets.reduce((sum, ns) => sum + (ns.materials?.timberList?.length ?? 0), 0),
    parseErrors: [],
    csvDelimiterLabel: 'COMMA',
  };
}

/**
 * Load staged hub timber CSV node sets for direct publish.
 */
export async function loadStagedTimberListNodeSets(
  rfqProjectId: string,
  hubFileReleaseId: string,
  _structureName = 'Staged hub timber list',
  proposalNodeSets?: ProposalNodeSet[] | null,
): Promise<HubTimberImportResult> {
  const release = await getRfqHubFileRelease(rfqProjectId, hubFileReleaseId);
  if (!release) {
    throw new Error('Staged hub timber release not found.');
  }
  const dealId = (release.dealId || '').trim();
  const { nodeSets, sourceFileName } = await loadStagedReleaseNodeSets(release, dealId, proposalNodeSets);
  return { nodeSets, sourceFileName };
}
